/**
 * @import Supercluster, { ClusterProperties } from 'supercluster'
 * @import { FeatureCollection, GeoJsonProperties, Point } from 'geojson'
 * @import { Place } from '@/features/world-map/types/place'
 */

import { usePlaceInfoContext } from '@/features/world-map/context/place-info';
import { usePlaces } from '@/features/world-map/hooks/use-places';
import { useZoom } from '@/hooks/use-zoom';
import { getBoundsZoomLevel } from '@/utils/map';
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo } from 'react';
import { useSupercluster } from '../hooks/use-supercluster';
import { ClusterMarker } from './cluster-marker';
import { PlaceInfo } from './place-info/place-info';
import { PlaceMarker } from './place-marker';

const minSize = 32;
const maxSize = 44;
const maxZoom = 6;
const uniformMarkerMaxZoom = 3; // Only keep markers uniform while at continent scale

/** @type {Supercluster.Options<GeoJsonProperties, ClusterProperties>} */
const superclusterOptions = {
  extent: 256,
  radius: 60,
  maxZoom,
};

export function PlacesLayer() {
  const map = useMap();
  const zoom = useZoom();
  const places = usePlaces();
  const { placeInfo, setPlaceInfo } = usePlaceInfoContext();

  /** @type {FeatureCollection<Point>} */
  const featureCollection = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: places.map((place, index) => ({
        type: 'Feature',
        id: index.toString(),
        geometry: {
          type: 'Point',
          coordinates: place.position,
        },
        properties: {
          place,
        },
      })),
    }),
    [places]
  );

  const { clusters, getClusterExpansionZoom, getLeaves } = useSupercluster(featureCollection, superclusterOptions);

  useEffect(() => {
    return () => {
      setPlaceInfo(null);
    };
  }, [setPlaceInfo]);

  const handleClusterClick =
    /**
     * @param {{ marker: google.maps.marker.AdvancedMarkerElement, clusterId: number | undefined }} event
     */
    ({ marker, clusterId }) => {
      setPlaceInfo(null);

      if (!map) {
        return;
      }

      if (clusterId) {
        const expansionZoom = getClusterExpansionZoom(clusterId);
        const leaves = getLeaves(clusterId);
        const bounds = new google.maps.LatLngBounds();
        leaves.forEach((leaf) =>
          bounds.extend({ lng: leaf.geometry.coordinates[0], lat: leaf.geometry.coordinates[1] })
        );
        const boundsZoom = getBoundsZoomLevel(bounds, map.getDiv()) - 0.5; // Subtract some to leave a bit of space

        // Use the greater of either the bounds or expansion; large clusters may not trigger expansion if their bounds
        // are too large.
        map.setZoom(Math.max(boundsZoom, expansionZoom));
        map.setCenter(bounds.getCenter());
        return;
      }

      if (marker.position) {
        map.setCenter(marker.position);
      }
      map.setZoom(maxZoom + 1);
    };

  return (
    <>
      {clusters.map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;

        const clusterProperties = feature.properties;
        if (clusterProperties?.cluster) {
          const clampedSize = Math.max(minSize, Math.min(maxSize, clusterProperties.point_count));
          const label = String(clusterProperties.point_count_abbreviated);

          return (
            <ClusterMarker
              key={feature.id}
              clusterId={clusterProperties.cluster_id}
              position={{ lat, lng }}
              size={clampedSize}
              label={label}
              ariaLabel={`${label} places`}
              onClick={handleClusterClick}
            />
          );
        }

        if (zoom <= uniformMarkerMaxZoom) {
          return (
            <ClusterMarker
              key={feature.id}
              position={{ lat, lng }}
              size={minSize}
              label="1"
              ariaLabel="1 port"
              onClick={handleClusterClick}
            />
          );
        }

        /** @type {Place} */
        // @ts-ignore
        const place = feature.properties.place;
        return (
          <PlaceMarker
            key={feature.id}
            ariaLabel={place.name}
            position={{ lng: place.position[0], lat: place.position[1] }}
            onClick={(marker) => setPlaceInfo({ place, marker })}
          />
        );
      })}
      {placeInfo && (
        <PlaceInfo
          key={placeInfo.place.position.join(',')}
          place={placeInfo.place}
          anchor={placeInfo.marker}
          onClose={() => setPlaceInfo(null)}
        />
      )}
    </>
  );
}
